import React, { createContext, useContext, useState, useEffect } from 'react';
import { Layout } from 'react-grid-layout';
import { DashboardWidget } from '../types';

type DashboardContextType = {
  widgets: DashboardWidget[];
  layout: Layout[];
  updateLayout: (newLayout: Layout[]) => void;
  addWidget: (widget: DashboardWidget) => void;
  removeWidget: (widgetId: string) => void;
  resetToDefault: () => void;
  isCustomizing: boolean;
  setIsCustomizing: (customizing: boolean) => void;
};

const defaultWidgets: DashboardWidget[] = [
  { id: 'health-score', type: 'health-score', title: 'Website Health Score', size: { w: 6, h: 4 }, minSize: { w: 4, h: 3 } },
  { id: 'traffic-chart', type: 'traffic-chart', title: 'Organic Traffic Trend', size: { w: 6, h: 4 }, minSize: { w: 4, h: 3 } },
  { id: 'keyword-rankings', type: 'keyword-rankings', title: 'Keyword Rankings', size: { w: 6, h: 6 }, minSize: { w: 4, h: 4 } },
  { id: 'recent-analyses', type: 'recent-analyses', title: 'Recent Analyses', size: { w: 6, h: 6 }, minSize: { w: 4, h: 4 } },
  { id: 'knowledge-entities', type: 'knowledge-entities', title: 'Knowledge Graph Entities', size: { w: 12, h: 5 }, minSize: { w: 8, h: 4 } },
  { id: 'geographic-analytics', type: 'geographic-analytics', title: 'Geographic Performance', size: { w: 6, h: 6 }, minSize: { w: 4, h: 5 } },
  { id: 'ab-testing', type: 'ab-testing', title: 'A/B Testing Dashboard', size: { w: 6, h: 6 }, minSize: { w: 4, h: 5 } }
];

const generateDefaultLayout = (widgets: DashboardWidget[]): Layout[] => {
  const layout: Layout[] = [];
  const currentY = 0;
  
  // First row: Health Score + Traffic Chart
  layout.push({
    i: 'health-score',
    x: 0,
    y: 0,
    w: 6,
    h: 4,
    minW: 4,
    minH: 3
  });
  
  layout.push({
    i: 'traffic-chart',
    x: 6,
    y: 0,
    w: 6,
    h: 4,
    minW: 4,
    minH: 3
  });
  
  // Second row: Keywords + Recent Analyses
  layout.push({
    i: 'keyword-rankings',
    x: 0,
    y: 4,
    w: 6,
    h: 6,
    minW: 4,
    minH: 4
  });
  
  layout.push({
    i: 'recent-analyses',
    x: 6,
    y: 4,
    w: 6,
    h: 6,
    minW: 4,
    minH: 4
  });
  
  // Third row: Knowledge Entities (full width)
  layout.push({
    i: 'knowledge-entities',
    x: 0,
    y: 10,
    w: 12,
    h: 5,
    minW: 8,
    minH: 4
  });
  
  // Fourth row: Geographic + A/B Testing
  layout.push({
    i: 'geographic-analytics',
    x: 0,
    y: 15,
    w: 6,
    h: 6,
    minW: 4,
    minH: 5
  });
  
  layout.push({
    i: 'ab-testing',
    x: 6,
    y: 15,
    w: 6,
    h: 6,
    minW: 4,
    minH: 5
  });
  
  return layout;
};

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [widgets, setWidgets] = useState<DashboardWidget[]>(defaultWidgets);
  const [layout, setLayout] = useState<Layout[]>([]);
  const [isCustomizing, setIsCustomizing] = useState(false);

  useEffect(() => {
    const savedLayout = localStorage.getItem('rnkrocket-dashboard-layout');
    const savedWidgets = localStorage.getItem('rnkrocket-dashboard-widgets');
    
    if (savedLayout && savedWidgets) {
      try {
        const parsedLayout = JSON.parse(savedLayout);
        const parsedWidgets = JSON.parse(savedWidgets);
        
        // Validate that layout items match widgets
        const validLayout = parsedLayout.filter((layoutItem: Layout) => 
          parsedWidgets.find((widget: DashboardWidget) => widget.id === layoutItem.i)
        );
        
        if (validLayout.length === parsedWidgets.length) {
          setLayout(validLayout);
          setWidgets(parsedWidgets);
        } else {
          // Fallback to default if data is inconsistent
          const defaultLayout = generateDefaultLayout(defaultWidgets);
          setLayout(defaultLayout);
          setWidgets(defaultWidgets);
        }
      } catch (error) {
        console.error('Error parsing saved dashboard data:', error);
        // Fall back to default
        const defaultLayout = generateDefaultLayout(defaultWidgets);
        setLayout(defaultLayout);
        setWidgets(defaultWidgets);
      }
    } else {
      // Generate default layout
      const defaultLayout = generateDefaultLayout(defaultWidgets);
      setLayout(defaultLayout);
    }
  }, []);

  const updateLayout = (newLayout: Layout[]) => {
    // Ensure layout items match existing widgets
    const validLayout = newLayout.filter(layoutItem => 
      widgets.find(widget => widget.id === layoutItem.i)
    );
    
    setLayout(validLayout);
    localStorage.setItem('rnkrocket-dashboard-layout', JSON.stringify(validLayout));
  };

  const addWidget = (widget: DashboardWidget) => {
    const newWidgets = [...widgets, widget];
    setWidgets(newWidgets);
    localStorage.setItem('rnkrocket-dashboard-widgets', JSON.stringify(newWidgets));
    
    // Find a good position for the new widget
    const maxY = Math.max(...layout.map(l => l.y + l.h), 0);
    const newLayoutEntry: Layout = {
      i: widget.id,
      x: 0,
      y: maxY,
      w: widget.size.w,
      h: widget.size.h,
      minW: widget.minSize?.w || 2,
      minH: widget.minSize?.h || 2
    };
    const newLayout = [...layout, newLayoutEntry];
    updateLayout(newLayout);
  };

  const removeWidget = (widgetId: string) => {
    const newWidgets = widgets.filter(w => w.id !== widgetId);
    setWidgets(newWidgets);
    localStorage.setItem('rnkrocket-dashboard-widgets', JSON.stringify(newWidgets));
    
    const newLayout = layout.filter(l => l.i !== widgetId);
    updateLayout(newLayout);
  };

  const resetToDefault = () => {
    setWidgets(defaultWidgets);
    localStorage.removeItem('rnkrocket-dashboard-layout');
    localStorage.removeItem('rnkrocket-dashboard-widgets');
    
    // Generate fresh default layout
    const defaultLayout = generateDefaultLayout(defaultWidgets);
    setLayout(defaultLayout);
  };

  return (
    <DashboardContext.Provider value={{
      widgets,
      layout,
      updateLayout,
      addWidget,
      removeWidget,
      resetToDefault,
      isCustomizing,
      setIsCustomizing
    }}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) throw new Error('useDashboard must be used within DashboardProvider');
  return context;
};